import React, { useState, useEffect } from 'react';
import { Edit } from 'lucide-react';
import api from '../services/api';
import {
    FileText,
    Trash2,
    Clock,
    Plus,
    FolderOpen,
    Download,
    Shield
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageHeader } from './ui/PageHeader';

const DocumentUpload: React.FC = () => {
    const [docs, setDocs] = useState<any[]>([]);
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState('General');
    const [file, setFile] = useState<File | null>(null);
    const [isAdding, setIsAdding] = useState(false);
  const [editingDoc, setEditingDoc] = useState<any>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editCategory, setEditCategory] = useState('General');

    useEffect(() => { fetchDocs(); }, []);

    const fetchDocs = async () => {
        const res = await api.get('/documents');
        setDocs(res.data);
    };

    const handleEdit = (doc: any) => {
        setEditingDoc(doc);
        setEditTitle(doc.title);
        setEditCategory(doc.category);
    };

    const handleEditSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingDoc) return;
        const formData = new FormData();
        formData.append('title', editTitle);
        formData.append('category', editCategory);
        try {
            await api.put(`/documents/${editingDoc.id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setEditingDoc(null);
            fetchDocs();
        } catch (err) {
            alert('Update failed');
        }
    };

    const handleUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!file) return;

        const formData = new FormData();
        formData.append('title', title);
        formData.append('category', category);
        formData.append('file', file);

        try {
            await api.post('/documents', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setTitle(''); setFile(null); setCategory('General');
            setIsAdding(false);
            fetchDocs();
        } catch (e) { alert('Upload failed'); }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this document?')) return;
        await api.delete(`/documents/${id}`);
        fetchDocs();
    };

    return (
        <div className="space-y-6">
            <PageHeader title="Resident Handbook" subtitle="Bye Laws, House Rules, and other documents">
                <button
                    onClick={() => setIsAdding(!isAdding)}
                    className="bg-violet-600 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-violet-700 transition-all text-sm"
                >
                    {isAdding ? 'Cancel' : 'Upload'}
                    <Plus size={18} className={cn("transition-transform", isAdding && "rotate-45")} />
                </button>
            </PageHeader>

            {isAdding && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 animate-in slide-in-from-top-2 duration-300">
                    <form onSubmit={handleUpload} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Document Title</label>
                                <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Annual Audit Report 2023" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" required />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Category</label>
                                <select value={category} onChange={e => setCategory(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500">
                                    <option>General</option>
                                    <option>Financial</option>
                                    <option>Legal</option>
                                    <option>Policy</option>
                                    <option>Meeting Minutes</option>
                                </select>
                            </div>
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-slate-500">File (PDF/Word)</label>
                            <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100" required />
                        </div>
                        <div className="flex justify-end pt-2">
                            <button type="submit" className="bg-slate-900 text-white px-6 py-2 rounded-lg font-bold text-sm hover:bg-slate-800 transition-all flex items-center gap-2">
                                Upload to Handbook <Shield size={16} />
                            </button>
                        </div>
                    </form>
                </div>
            )}
            {editingDoc && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mt-4 animate-in slide-in-from-top-2 duration-300">
                    <h3 className="text-lg font-bold mb-3">Edit Document</h3>
                    <form onSubmit={handleEditSave} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Document Title</label>
                                <input value={editTitle} onChange={e => setEditTitle(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" required />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Category</label>
                                <select value={editCategory} onChange={e => setEditCategory(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500">
                                    <option>General</option>
                                    <option>Financial</option>
                                    <option>Legal</option>
                                    <option>Policy</option>
                                    <option>Meeting Minutes</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex justify-end pt-2">
                            <button type="submit" className="bg-violet-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-violet-700 transition-all">
                                Save Changes
                            </button>
                            <button type="button" onClick={() => setEditingDoc(null)} className="ml-2 text-slate-600 hover:underline">
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {docs.map((d) => (
                    <div key={d.id} className="bg-white p-5 rounded-xl border border-slate-200 hover:border-violet-200 transition-all group">
                        <div className="flex justify-between items-start mb-4">
                            <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center text-violet-600">
                                <FileText size={20} />
                            </div>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                                {d.category}
                            </span>
                        </div>
                        <h4 className="font-bold text-slate-900 group-hover:text-violet-600 transition-colors leading-snug mb-4">{d.title}</h4>
                        <div className="pt-4 border-t border-slate-50 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-slate-400">
                                <Clock size={12} />
                                <span className="text-[10px] font-bold uppercase">{new Date(d.created_at).toLocaleDateString()}</span>
                            </div>
                            <div className="flex gap-2">
                                <a href={d.file_url} target="_blank" rel="noreferrer" className="p-1.5 bg-slate-50 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-50">
                                    <Download size={16} />
                                </a>
                                <button onClick={() => handleEdit(d)} className="p-1.5 bg-slate-50 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-50">
                                    <Edit size={16} />
                                </button>
                                <button onClick={() => handleDelete(d.id)} className="p-1.5 bg-slate-50 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50">
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
                {docs.length === 0 && (
                    <div className="col-span-full py-12 text-center bg-white rounded-xl border border-dashed border-slate-200">
                        <FolderOpen size={32} className="text-slate-200 mx-auto mb-2" />
                        <p className="text-sm text-slate-400 font-medium">The vault is currently empty</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DocumentUpload;
